import { defineStore } from 'pinia'
import onboardingService from '@/services/onboarding.service'
import { useAuthStore } from '@/pinia/auth'

// Module-level poll state (not reactive — avoids Pinia reactivity on timer handles)
let _pollTimer = null
let _pollResolve = null

export const useOnboardingStore = defineStore({
  id: 'onboarding',
  state: () => ({
    status: null,
    feeTransaction: null,
    loading: false,
    polling: false,
  }),
  getters: {
    onboardingStatus() {
      return this.status || 'registered'
    },
    isRegistered() {
      return this.onboardingStatus === 'registered'
    },
    isTrial() {
      return this.onboardingStatus === 'trial'
    },
    isGoLivePending() {
      return this.onboardingStatus === 'go_live_pending'
    },
    isPaymentPending() {
      return this.onboardingStatus === 'payment_pending'
    },
    isOnboarding() {
      return this.onboardingStatus === 'onboarding'
    },
    isCompleted() {
      return this.onboardingStatus === 'completed'
    },
    /**
     * Show the import wizard only when onboarding is in progress
     * (fee paid, data wiped, ready to upload master data).
     */
    showImportWizard() {
      return this.onboardingStatus === 'onboarding' || this.onboardingStatus === 'completed'
    },
    /**
     * Show the onboarding flow dispatcher (trial start, go-live, fee payment).
     */
    showOnboardingFlow() {
      return ['registered', 'trial', 'go_live_pending', 'payment_pending'].includes(this.onboardingStatus)
    },
  },
  actions: {
    async fetchStatus() {
      this.loading = true
      try {
        const response = await onboardingService.getStatus()
        const data = response.data && response.data.data
        if (data) {
          this.status = data.onboarding_status
          // Sync to auth store
          const authStore = useAuthStore()
          authStore.updateEntity({
            onboarding_status: data.onboarding_status,
            onboarding_fee_paid_at: data.onboarding_fee_paid_at,
          })
        }
      } catch {
        // Silently fail
      } finally {
        this.loading = false
      }
    },

    async startTrial(withDemoData = true) {
      const response = await onboardingService.startTrial(withDemoData)
      const data = response.data && response.data.data
      if (data) {
        this.status = data.onboarding_status
        const authStore = useAuthStore()
        authStore.updateSubscriptionStatus(data.subscription_status)
        authStore.updateEntity({ onboarding_status: data.onboarding_status })
      }
      return data
    },

    async goLive() {
      const response = await onboardingService.goLive()
      const data = response.data && response.data.data
      if (data) {
        this.status = data.onboarding_status
        const authStore = useAuthStore()
        authStore.updateSubscriptionStatus(data.subscription_status)
        authStore.updateEntity({ onboarding_status: data.onboarding_status })
      }
      return data
    },

    async checkoutFee(paymentChannel) {
      const response = await onboardingService.checkoutFee(paymentChannel)
      const data = response.data && response.data.data
      if (data) {
        this.feeTransaction = data
        this.status = 'payment_pending'
        const authStore = useAuthStore()
        authStore.updateEntity({ onboarding_status: 'payment_pending' })
      }
      return data
    },

    async fetchFeeTransaction() {
      try {
        const response = await onboardingService.getFeeTransaction()
        const data = response.data && response.data.data
        this.feeTransaction = data
        return data
      } catch {
        return null
      }
    },

    /**
     * One-shot status check (does not start polling).
     */
    async checkFeeStatusOnce(txId) {
      const response = await onboardingService.getFeeTransactionStatus(txId)
      const data = response.data && response.data.data
      if (data) {
        if (data.status === 'paid') {
          this.status = data.onboarding_status
          const authStore = useAuthStore()
          authStore.updateEntity({ onboarding_status: data.onboarding_status })
          return true
        }
        if (data.status === 'expired' || data.status === 'cancelled') {
          return false
        }
      }
      return null
    },

    async pollFeeStatus(txId) {
      if (_pollTimer) return
      this.polling = true

      const poll = async () => {
        try {
          const response = await onboardingService.getFeeTransactionStatus(txId)
          const data = response.data && response.data.data
          if (data) {
            if (data.status === 'paid') {
              this.status = data.onboarding_status
              const authStore = useAuthStore()
              authStore.updateEntity({ onboarding_status: data.onboarding_status })
              this.stopPolling()
              return true
            }
            if (data.status === 'expired' || data.status === 'cancelled') {
              this.stopPolling()
              return false
            }
          }
        } catch {
          // Continue polling
        }
        return null
      }

      // Initial check
      const result = await poll()
      if (result !== null) return result

      // Poll every 5 seconds
      return new Promise((resolve) => {
        _pollResolve = resolve
        _pollTimer = setInterval(async () => {
          const r = await poll()
          if (r !== null) resolve(r)
        }, 5000)
      })
    },

    stopPolling() {
      if (_pollTimer) {
        clearInterval(_pollTimer)
        _pollTimer = null
      }
      // Resolve any pending promise so callers don't hang
      if (_pollResolve) {
        _pollResolve(null)
        _pollResolve = null
      }
      this.polling = false
    },
  },
})
